import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, ShieldOff } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

interface DemoLoginConfig {
  enabled: boolean;
  updated_at?: string | null;
  updated_by?: string | null;
}

/**
 * Super-admin card that toggles the public "Continue as Judge" (demo-login)
 * button on the Login page. Judges never get admin access — the endpoint
 * always hands out a Pro-tier trial account with role="user". This card only
 * controls whether the endpoint is exposed at all.
 */
export const DemoLoginCard: React.FC = () => {
  const { user } = useAuth();
  const isSuperAdmin = !!(user as any)?.is_super_admin;
  const [cfg, setCfg] = useState<DemoLoginConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get<DemoLoginConfig>("/admin/demo-login/config");
      setCfg(data);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Failed to load demo-login config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isSuperAdmin) load(); }, [isSuperAdmin]);

  const save = async (enabled: boolean) => {
    setSaving(true);
    try {
      const { data } = await api.put<DemoLoginConfig>("/admin/demo-login/config", { enabled });
      setCfg(data);
      toast.success(enabled ? "Demo access enabled" : "Demo access disabled");
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Failed to update config");
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperAdmin) return null;

  const on = !!cfg?.enabled;

  return (
    <div
      className="sg-card p-5 sm:p-6 space-y-4"
      data-testid="demo-login-card"
    >
      <div className="flex items-start gap-3">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${on ? "bg-fuchsia-100 text-fuchsia-700" : "bg-slate-100 text-slate-500"}`}>
          {on ? <Sparkles className="h-5 w-5" /> : <ShieldOff className="h-5 w-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[color:var(--sg-fg)]">
            "Continue as Demo" button
          </div>
          <p className="text-sm text-[color:var(--sg-muted)] mt-0.5 leading-relaxed">
            One-click demo login on the public <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">/login</code> page. Visitors land on a <b>Pro-tier demo account with a 14-day free trial</b> — <b>zero administrative access</b> (no admin, no super-admin, no data mutations on shared workspaces).
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-[color:var(--sg-border)] bg-[color:var(--sg-muted-bg)]/40 p-4 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest text-[color:var(--sg-muted)] mb-1">
            Current status
          </div>
          <div className={`inline-flex items-center gap-1.5 text-sm font-semibold ${on ? "text-emerald-700" : "text-slate-600"}`}>
            <span className={`h-2 w-2 rounded-full ${on ? "bg-emerald-500 animate-pulse" : "bg-slate-400"}`} />
            {loading ? "Loading…" : on ? "Demo access is ON" : "Demo access is OFF"}
          </div>
          {cfg?.updated_at && (
            <div className="text-[11px] text-[color:var(--sg-muted)] mt-1">
              Last updated {new Date(cfg.updated_at).toLocaleString()} · by {cfg.updated_by || "admin"}
            </div>
          )}
        </div>

        <button
          type="button"
          disabled={loading || saving}
          onClick={() => save(!on)}
          data-testid="demo-login-toggle"
          aria-pressed={on}
          className={`relative inline-flex h-7 w-14 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[color:var(--sg-violet)] disabled:opacity-50 ${on ? "bg-fuchsia-500" : "bg-slate-300"}`}
        >
          {saving && (
            <Loader2 className="absolute left-1/2 top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 animate-spin text-white" />
          )}
          <span
            className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition ${on ? "translate-x-7" : "translate-x-0.5"}`}
          />
        </button>
      </div>

      <div className="text-[11px] text-[color:var(--sg-muted)] leading-relaxed">
        Turn this OFF once your public campaign ends to fully close the demo entrypoint. Existing demo sessions remain active until their token expires; new logins are refused with a 403.
      </div>
    </div>
  );
};

export default DemoLoginCard;
